import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkEmoji from 'remark-emoji';
import remarkCalloutBlockquote from '../../../utils/remarkCalloutBlockquote';
import { useParams } from 'react-router-dom';
import CyberNav from '../CyberNav/CyberNav';
import ArticlesList from './ArticlesList';
import DecryptionText from './DecryptionText';

// Import shared utilities and components
import { detectLanguageDirection, wrapWithLanguage } from '../../../utils/languageUtils';
import { extractMetadata, ArticleMetadata } from '../../../utils/markdownUtils';
import useResponsive from '../../../utils/useResponsive';
import ErrorBoundary from '../../Shared/ErrorBoundary';
import CodeBlock from '../../Shared/CodeBlock';

// CSS imports
import './Markdown-Global.css';    // 1. Base styles
import './CyberPages.css';        // 2. Cyber specific layout

// No local implementations needed - using shared components and utilities

const CyberGuides = () => {
  const { fileName } = useParams();
  const [content, setContent] = useState('');
  const [metadata, setMetadata] = useState({});
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBox, setShowBox] = useState(true);
  // const { isMobile } = useResponsive();

  useEffect(() => {
    if (fileName) {
      const fetchContent = async () => {
        setLoading(true);
        try {
          const response = await fetch(`/md/CyberGuides/${fileName}`);
          if (!response.ok) throw new Error('Content not found');
          const text = await response.text();
          const { content: parsedContent, metadata: parsedMetadata } = extractMetadata(text);
          setContent(parsedContent);
          setMetadata(parsedMetadata);
          setError(null);
        } catch (err) {
          setError(err.message);
          setContent('');
          setMetadata({});
        } finally {
          setLoading(false);
        }
      };

      fetchContent();
    }
  }, [fileName]);

  // Helper to wrap text in DecryptionText
  const withDecryption = (children) => {
    return React.Children.map(children, child => {
      if (typeof child === 'string' && child.trim().length > 0) {
        return <DecryptionText text={child} />;
      }
      if (React.isValidElement(child) && child.props.children) {
        return React.cloneElement(child, {
          children: withDecryption(child.props.children)
        });
      }
      return child;
    });
  };

  return (
    <div className="cyber-wrapper">
      {!fileName && showBox && (
        <div className="rtl-box">
          <button
            className="close-btn"
            onClick={() => setShowBox(false)}
            aria-label="Close welcome message"
          >
            ×
          </button>
          <p>ברוכים הבאים למדריכי הסייבר שלנו! כאן תמצאו מידע מקיף על אבטחת מידע והגנת סייבר.</p>
          <p>המדריכים מכסים נושאים מגוונים, החל מיסודות האבטחה ועד לטכניקות מתקדמות.</p>
        </div>
      )}

      <div className="cyber-page-layout">
        <CyberNav />
        {fileName ? (
          <div className="markdown-content" lang={detectLanguageDirection(content)}>
            {loading ? (
              <div className="skeleton-loader">
                <div className="skeleton-header"></div>
                <div className="skeleton-text"></div>
                <div className="skeleton-text"></div>
                <div className="skeleton-text"></div>
              </div>
            ) : error ? (
              <div className="error-container">
                <p className="error">{error}</p>
                <p>Try refreshing the page or contact support if the problem persists.</p>
              </div>
            ) : (
              <>
                <ArticleMetadata metadata={metadata} />
                <ErrorBoundary>
                  <ReactMarkdown
                    children={content}
                    
                    remarkPlugins={[remarkGfm, remarkEmoji, remarkCalloutBlockquote]}
                    components={{
                      h1: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h1 {...props}>{withDecryption(wrapWithLanguage(children, lang, true))}</h1>;
                      },
                      h2: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h2 {...props}>{withDecryption(wrapWithLanguage(children, lang, true))}</h2>;
                      },
                      h3: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h3 {...props}>{withDecryption(wrapWithLanguage(children, lang, true))}</h3>;
                      },
                      h4: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h4 {...props}>{withDecryption(wrapWithLanguage(children, lang, true))}</h4>;
                      },
                      h5: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h5 {...props}>{withDecryption(wrapWithLanguage(children, lang, true))}</h5>;
                      },
                      h6: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <h6 {...props}>{withDecryption(wrapWithLanguage(children, lang, true))}</h6>;
                      },
                      p: ({ node, children, ...props }) => {
                        const lang = detectLanguageDirection(String(children));
                        return <p {...props} lang={lang}>{withDecryption(wrapWithLanguage(children, lang, false))}</p>;
                      },
                      li: ({ children, ...props }) => <li {...props}>{withDecryption(children)}</li>,
                      strong: ({ children, ...props }) => <strong {...props}>{withDecryption(children)}</strong>,
                      em: ({ children, ...props }) => <em {...props}>{withDecryption(children)}</em>,
                      del: ({ children, ...props }) => <del {...props}>{withDecryption(children)}</del>,
                      blockquote: (renderProps) => {
                        const { node, children, ...props } = renderProps;
                        const p = node?.properties || props || {};
                        const calloutType = p['data-callout-type'];
                        const calloutTitle = p['data-callout-title'];
                        const calloutOpen = p['data-callout-open'] === 'true';
                        const collapsible = p['data-callout-collapsible'] === 'true';
                        if (calloutType != null) {
                          const type = String(calloutType).toLowerCase();
                          const title = (calloutTitle != null ? String(calloutTitle) : calloutType).trim();
                          const icon = type === 'warning' ? '⚠️' : type === 'danger' || type === 'error' ? '🚨' : type === 'success' || type === 'check' ? '✅' : type === 'note' ? '📝' : type === 'tip' ? '💡' : type === 'bug' ? '🐛' : 'ℹ️';
                          if (collapsible) {
                            return (
                              <details className={`markdown-callout callout-${type}`} open={calloutOpen}>
                                <summary className="callout-title" dir="auto">
                                  <div className="callout-title-inner">
                                    <span className="callout-icon">{icon}</span>
                                    <span className="callout-title-text"><DecryptionText text={title} /></span>
                                  </div>
                                  <span className="callout-toggle-icon" />
                                </summary>
                                <div className="callout-content" dir="auto">
                                  {withDecryption(children)}
                                </div>
                              </details>
                            );
                          }
                          return (
                            <div className={`markdown-callout callout-${type}`}>
                              <div className="callout-title" dir="auto">
                                <div className="callout-title-inner">
                                  <span className="callout-icon">{icon}</span>
                                  <span className="callout-title-text"><DecryptionText text={title} /></span>
                                </div>
                              </div>
                              <div className="callout-content" dir="auto">
                                {withDecryption(children)}
                              </div>
                            </div>
                          );
                        }
                        return <blockquote {...props}>{withDecryption(children)}</blockquote>;
                      },
                      td: ({ children, ...props }) => <td {...props}>{withDecryption(children)}</td>,
                      th: ({ children, ...props }) => <th {...props}>{withDecryption(children)}</th>,
                      code: ({ node, inline, className, children, ...props }) => {
                        const match = /language-(\w+)/.exec(className || '');
                        return !inline && match ? (
                          <CodeBlock className={className} {...props}>
                            {children}
                          </CodeBlock>
                        ) : (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  />
                </ErrorBoundary>
              </>
            )}
          </div>
        ) : (
          <ErrorBoundary>
            <ArticlesList
              folderName="CyberGuides"
              basePath="cyber/hacking/guides"
              defaultCategory="Cyber Guide"
            />
          </ErrorBoundary>
        )}
      </div>
    </div>
  );
};

export default CyberGuides;
